import { FC, MouseEvent, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ProcedureContext } from "@/context/ProcedureContext";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionIcon from "@mui/icons-material/Description";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FastForwardIcon from "@mui/icons-material/FastForward";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	ClickAwayListener,
	Divider,
	Grow,
	PaginationItem,
} from "@mui/material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	Grid,
	IconButton,
	LinearProgress,
	List,
	MenuItem,
	MenuList,
	Pagination,
	Paper,
	Popper,
	Skeleton,
	Stack,
	Switch,
	Tooltip,
	Typography,
	alpha,
} from "@mui/material";
import { green, grey, red, yellow } from "@mui/material/colors";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import { cleanDigitSectionValue } from "node_modules/@mui/x-date-pickers/esm/internals/hooks/useField/useField.utils";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { SATOSLink } from "@/components/Application/SATOSLink";
import { Translate } from "@/components/Application/Translate";
import NewChecklistModal from "@/components/Procedures/NewChecklist/NewChecklistModal";
import {
	ChecklistTypes,
	CreateChecklistRequest,
} from "@/components/Procedures/Types/ManualChecklistTypes";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import Spinner from "@/helpers/components/Spinner";

import { ProcedureAccordionMove } from "@/model/Procedure/ProcedureAccordionMove";
import { ProcedureAccordionMoveDirection } from "@/model/Procedure/ProcedureAccordionMoveDirection";
import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";
import { ProcedureDetailNodeState } from "@/model/Procedure/ProcedureDetailNodeState";
import { UserContext } from "@/model/User/UserContext";

import { checkAdminAccess } from "@/service/ApplicationQueries";
import {
	UpdateProcedureDetailsTree,
	UpsertProcedureNodeSteps,
	useCreateChecklist,
} from "@/service/ProcedureMutations";
import { useFetchActiveProcedureDetails } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);
dayjs.extend(duration);

interface IAccordionSingle {
	openDetailNodes: Set<number>;
	activeSchemeId: number;
	setActiveSchemeId: (schemeId: number) => void;
	toggleOpenDetailNodes: (
		node: ProcedureDetailNode,
		forceOpen: boolean,
	) => void;
}

interface IAccordion extends IAccordionSingle {
	editMode: boolean | undefined;
	readonly: boolean;
	showSingleSchemeId: number;
	toggleEditNode: (editNode: boolean) => void;
	toggleEditChecklist: (editChecklist: boolean) => void;
	paginationEnabled: boolean;
	setPaginationEnabled: (enabled: boolean) => void;
}

export const AccordionSingle: FC<IAccordion> = (props) => {
	const { data: procedureDetails, isLoading } =
		useFetchActiveProcedureDetails();
	const [currentPage, setCurrentPage] = useState(0);
	const itemsPerPage = 5;

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	// keep this BEFORE any early returns
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		const check = () => {
			// measure after layout paints
			const over = el.scrollHeight > el.clientHeight + 1; // +1 to avoid off-by-one flicker
			setIsOverflowing(over);
		};

		// initial check
		check();

		// observe size changes of the scroll area
		const ro = new ResizeObserver(check);
		ro.observe(el);

		// also listen to window resizes (fonts/viewport changes)
		window.addEventListener("resize", check);

		return () => {
			window.removeEventListener("resize", check);
			ro.disconnect();
		};
	}, []);

	// re-check when list content changes (page, items, pagination toggle)
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const over = el.scrollHeight > el.clientHeight + 1;
		setIsOverflowing(over);
	}, [procedureDetails, currentPage, itemsPerPage, props.paginationEnabled]);

	const user = useContext(UserContext);
	const isAdmin = user && checkAdminAccess(user);
	if (isLoading) {
		return (
			<section className="accordion-wrapper ">
				<List
					className="accordion-menu"
					sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
				>
					<Skeleton variant="rectangular" height={36} />
					<Skeleton variant="rectangular" height={84} />
					<Skeleton variant="rectangular" height={84} />
					<Skeleton variant="rectangular" height={84} />
					<Skeleton variant="rectangular" height={84} />
				</List>
			</section>
		);
	}

	const totalItems = procedureDetails?.ProcedureDetailNodes.length ?? 0;
	const totalPages = Math.ceil(totalItems / itemsPerPage);

	const itemsToShow = props.paginationEnabled
		? procedureDetails?.ProcedureDetailNodes.slice(
				currentPage * itemsPerPage,
				(currentPage + 1) * itemsPerPage,
			)
		: procedureDetails?.ProcedureDetailNodes;

	return (
		<Box
			component="section"
			className="accordion-wrapper"
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.5,
				flex: 1,
				minHeight: 0,
				height: "100%",
			}}
		>
			{/* 🔹 Scrollable area that contains both items AND the pager */}
			<Box
				ref={scrollRef}
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					pr: 1,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<List
					sx={{
						p: 0,
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					{itemsToShow?.map((node) => (
						<ProcedureAccordionMenuItem
							{...props}
							key={node.SchemeId}
							item={node}
							isAdmin={isAdmin}
						/>
					))}
				</List>

				{/* 🔹 Pagination INSIDE the scroll area, under the items.
            It becomes sticky when content overflows; otherwise it stays in normal flow. */}
				{props.paginationEnabled && totalPages > 1 && (
					<Box
						sx={{
							position: isOverflowing ? "sticky" : "static",
							bottom: isOverflowing ? 0 : "auto",
							bgcolor: "background.paper",
							borderTop: (t) => `1px solid ${t.palette.divider}`,
							px: 1.5,
							py: 1,
							// ensure it sits above content when sticky
							zIndex: 1,
						}}
					>
						<Pagination
							count={totalPages}
							page={currentPage + 1}
							onChange={(_e, page) => setCurrentPage(page - 1)}
							color="primary"
							size="small"
							renderItem={(item) => (
								<PaginationItem
									{...item}
									shape="rounded"
									variant="outlined"
									sx={{
										borderRadius: 999,
										minWidth: 34,
										height: 30,
										fontWeight: 600,
										borderColor: "divider",
										bgcolor: "background.paper",
										"&:hover": { boxShadow: 2, borderColor: "primary.light" },
										"&.Mui-focusVisible": { boxShadow: 3 },
										"&.Mui-selected": {
											bgcolor: "primary.main",
											color: "primary.contrastText",
											borderColor: "primary.main",
											"&:hover": { bgcolor: "primary.dark" },
										},
										"&.MuiPaginationItem-previousNext": { px: 1.25 },
										"&.MuiPaginationItem-ellipsis": { mx: 0.5 },
									}}
								/>
							)}
							sx={{
								"& .MuiPagination-ul": {
									gap: 0.5,
									justifyContent: "flex-start",
								},
							}}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

interface IAccordionItem extends IAccordionSingle {
	item: ProcedureDetailNode;
	readonly: boolean;
	openDetailNodes: Set<number>;
	toggleEditNode: (editNode: boolean) => void;
	toggleEditChecklist: (editChecklist: boolean) => void;
}

interface IAccordionMainItem extends IAccordionItem {
	isAdmin?: boolean;
	editMode: boolean | undefined;
	readonly: boolean;
	showSingleSchemeId: number;
	toggleOpenDetailNodes: (
		node: ProcedureDetailNode,
		forceOpen: boolean,
	) => void;
}

const moveAccordionItem = async (
	event: MouseEvent,
	node: ProcedureDetailNode,
	direction: ProcedureAccordionMoveDirection,
	procedureId: number,
	updateTree: (payload: ProcedureAccordionMove) => void,
) => {
	event.stopPropagation();

	const schemeId = node.SchemeId;
	const procedureAccordionMove: ProcedureAccordionMove = {
		SchemeId: schemeId,
		ProcedureId: procedureId,
		Direction: direction,
		Node: node,
	};

	await updateTree(procedureAccordionMove);
};

const ProcedureAccordionMenuItem: FC<IAccordionMainItem> = (props) => {
	const { id } = useParams<RouteParams>();
	const { setActiveStep } = useContext(ProcedureContext);
	const updateProcedureDetailsTree = UpdateProcedureDetailsTree();
	const [hoverId, setHoverId] = useState<number>();
	const [open, setOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [newChecklistOpen, toggleNewChecklist] = useState<boolean>(false);

	const anchorRef = useRef<HTMLButtonElement>(null);
	const navigate = useNavigate();

	const handleToggle = () => setOpen((prev) => !prev);

	const handleClose = (event: Event | React.SyntheticEvent) => {
		if (
			anchorRef.current &&
			anchorRef.current.contains(event.target as HTMLElement)
		)
			return;
		setOpen(false);
	};

	if (
		!props.item ||
		(props.showSingleSchemeId > 0 &&
			props.item.SchemeId != props.showSingleSchemeId)
	) {
		return <></>;
	}

	const isActive =
		props.openDetailNodes.has(props.item.TreeNodeNumber) ||
		props.openDetailNodes.has(props.item.SchemeId);

	const hasChildren = !!props.item.Children?.length;

	const saveChecklist = UpsertProcedureNodeSteps();
	const createChecklist = useCreateChecklist();

	const handleSubmit = async () => {
		setIsSubmitting(true);
		try {
			const NodeId = props.item.SchemeId;
			const request: CreateChecklistRequest = {
				ProcedureId: +id,
				NodeId,
				ChecklistType: ChecklistTypes.ManualChecklist,
			};
			const response = await createChecklist(request);
			AlertRequest.success();
			if (response.ChecklistId) {
				setActiveStep(-1);
				navigate(`/procedure/${id}/checklist/${response.ChecklistId}/edit`);
			}
		} catch (error) {
			AlertRequest.failed();
			console.error(error);
		} finally {
			setIsSubmitting(false);
			handleToggle();
		}
	};

	const hasDocument = (node: ProcedureDetailNode | undefined) =>
		node?.DocumentAttached || false;

	if (isSubmitting) return <Spinner text="Saving..." />;

	return (
		<>
			<Box
				onMouseEnter={() => setHoverId(+props.item.SchemeId)}
				onMouseLeave={() => setHoverId(undefined)}
			>
				{(() => {
					const pct = Math.round(
						(Number(props.item.Done || 0) /
							Math.max(1, Number(props.item.Total || 0))) *
							100,
					);
					const statusColor = calculateStatusColor(props.item.CompletedState);

					return (
						<Accordion
							defaultExpanded={isActive}
							expanded={isActive}
							disableGutters
							onChange={() => props.toggleOpenDetailNodes(props.item, false)}
							square={false}
							sx={{
								position: "relative",
								width: "100%",
								borderRadius: 2,
								boxShadow: "none",
								border: `1px solid ${alpha(statusColor, 0.35)}`,
								overflow: "visible",
								transition: (t) =>
									t.transitions.create(
										["box-shadow", "border-color", "transform"],
										{
											duration: t.transitions.duration.shorter,
										},
									),
								"&:hover": {
									boxShadow: 4,
									borderColor: statusColor,
									transform: "translateY(-1px)",
								},
								"&::before": { display: "none" },
								"&::after": {
									content: '""',
									position: "absolute",
									left: 6,
									top: 8,
									bottom: 8,
									width: 6,
									borderRadius: 6,
									backgroundColor: statusColor,
									pointerEvents: "none",
								},
							}}
						>
							<AccordionSummary
								expandIcon={
									hasChildren ? (
										<KeyboardArrowDownIcon />
									) : (
										<Box sx={{ width: 24, height: 24 }} />
									)
								}
								aria-controls={`node-${props.item.SchemeId}-content`}
								id={`node-${props.item.SchemeId}-summary`}
								sx={{
									minHeight: 56,
									px: 2,
									pl: 3.5, // room for the left accent
									"& .MuiAccordionSummary-content": {
										my: 0.5,
										display: "flex", //
										alignItems: "center",
										gap: 1.5,
										minWidth: 0,
									},
								}}
							>
								{/* LEFT: title + doc */}
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
								>
									<Tooltip title={props.item.Name}>
										<Typography
											variant="subtitle1"
											noWrap
											sx={{
												fontWeight: 700,
												color: "text.primary",
												minWidth: 0,
											}}
										>
											{props.item.Name}
										</Typography>
									</Tooltip>

									{props.item.DocumentAttached && (
										<Tooltip title="Open attached document in a new tab" arrow>
											<IconButton
												size="small"
												component="a"
												href={`/api/Documents/${props.item.FileId}/html`}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
												aria-label="open-document"
												sx={{ width: 28, height: 28 }}
											>
												<DescriptionIcon fontSize="inherit" />
											</IconButton>
										</Tooltip>
									)}
								</Stack>

								{/* RIGHT: meta (with vertical dividers) */}
								<Stack
									direction="row"
									alignItems="center"
									spacing={1.25}
									sx={{ flexShrink: 0 }}
								>
									{Boolean(props.item.NodeHasUpdateId) && (
										<Tooltip
											title={
												props.isAdmin ? (
													<Translate String="Procedure.Changes.Admin">
														Review and approve changes
													</Translate>
												) : (
													<Translate String="Procedure.Changes.User">
														Updates available – ask an administrator to review
													</Translate>
												)
											}
											arrow
										>
											<span>
												{props.isAdmin ? (
													<SATOSLink
														to="viewuppdatednode"
														params={[id, props.item.SchemeId]}
													>
														<IconButton
															size="small"
															sx={{ color: (t) => t.palette.warning.main }}
														>
															<WarningAmberIcon fontSize="inherit" />
														</IconButton>
													</SATOSLink>
												) : (
													<IconButton
														size="small"
														disabled
														sx={{
															color: (t) => t.palette.warning.main,
														}}
													>
														<WarningAmberIcon fontSize="inherit" />
													</IconButton>
												)}
											</span>
										</Tooltip>
									)}

									{/* Progress + status */}
									<Stack direction="row" alignItems="center" spacing={1}>
										<Tooltip
											title={`${props.item.Done} of ${props.item.Total} completed (${pct}%)`}
											arrow
										>
											<Chip
												size="small"
												variant="outlined"
												label={`${props.item.Done}/${props.item.Total} • ${pct}%`}
												sx={{ fontWeight: 600 }}
											/>
										</Tooltip>

										<Tooltip
											title={`Status: ${props.item.CompletedState}`}
											arrow
										>
											<Box
												component="span"
												sx={{
													width: 12,
													height: 12,
													borderRadius: "50%",
													bgcolor: statusColor,
													border: (t) =>
														`1px solid ${alpha(t.palette.common.black, 0.1)}`,
													flex: "0 0 12px",
												}}
												aria-label={`Status: ${props.item.CompletedState}`}
											/>
										</Tooltip>
									</Stack>

									{/* Edit (admin) */}
									{!props.readonly && (
										<AdminProtected>
											<Tooltip title="Edit node" arrow>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														props.setActiveSchemeId(
															props.item.Children.length
																? props.item.Children[0].SchemeId
																: props.item.SchemeId,
														);
														props.toggleEditNode(true);
														props.toggleEditChecklist(false);
													}}
													aria-label="edit-node"
													sx={{ width: 28, height: 28 }}
												>
													<EditOutlinedIcon fontSize="inherit" />
												</IconButton>
											</Tooltip>
										</AdminProtected>
									)}

									{/* Hover admin actions (add/move) */}
									<AdminProtected>
										<Stack
											direction="row"
											alignItems="center"
											spacing={0.5}
											sx={{
												opacity: hoverId === +props.item.SchemeId ? 1 : 0,
												pointerEvents:
													hoverId === +props.item.SchemeId ? "auto" : "none",
												transition: "opacity .15s ease",
												"& .MuiIconButton-root": {
													width: 28,
													height: 28,
													p: 0.25,
												},
											}}
										>
											<Tooltip
												title={
													<Translate String="Node.Add.Checklist">
														Add checklist
													</Translate>
												}
												arrow
											>
												<IconButton
													ref={anchorRef}
													onClick={(e) => {
														e.stopPropagation();
														handleToggle();
													}}
													aria-label="add-checklist"
													size="small"
												>
													<AddCircleOutlineIcon fontSize="inherit" />
												</IconButton>
											</Tooltip>

											<Stack direction="column" spacing={0.25}>
												<Tooltip
													title={
														<Translate String="Node.Move.Up">Move Up</Translate>
													}
													placement="top"
													arrow
												>
													<IconButton
														size="small"
														aria-label="move-up"
														onClick={(e) =>
															moveAccordionItem(
																e,
																props.item,
																"Up",
																parseInt(id),
																updateProcedureDetailsTree,
															)
														}
													>
														<KeyboardArrowUpIcon fontSize="inherit" />
													</IconButton>
												</Tooltip>
												<Tooltip
													title={
														<Translate String="Node.Move.Down">
															Move Down
														</Translate>
													}
													placement="bottom"
													arrow
												>
													<IconButton
														size="small"
														aria-label="move-down"
														onClick={(e) =>
															moveAccordionItem(
																e,
																props.item,
																"Down",
																parseInt(id),
																updateProcedureDetailsTree,
															)
														}
													>
														<KeyboardArrowDownIcon fontSize="inherit" />
													</IconButton>
												</Tooltip>
											</Stack>
										</Stack>
									</AdminProtected>
								</Stack>
							</AccordionSummary>

							{/* CHILDREN */}
							{hasChildren && (
								<Box sx={{ textAlign: "left" }}>
									<AccordionDetails sx={{ py: 1.5, px: 2 }}>
										<Box sx={{ mb: 1 }}>
											<LinearProgress
												variant="determinate"
												value={pct}
												sx={{
													height: 6,
													borderRadius: 999,
													bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
													"& .MuiLinearProgress-bar": {
														borderRadius: 999,
														backgroundColor: statusColor,
													},
												}}
												aria-label={`Progress ${pct}%`}
											/>
										</Box>

										{props.item.Children.map((child) => (
											<ProcedureAccordionSubMenuItem
												{...props}
												key={child.SchemeId}
												item={child}
											/>
										))}
									</AccordionDetails>
								</Box>
							)}
						</Accordion>
					);
				})()}
			</Box>

			{/* POPPER: Add checklist menu */}
			<Popper
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				placement="bottom-start"
				transition
				sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin:
								placement === "bottom-start" ? "left top" : "left bottom",
						}}
					>
						<Paper elevation={3}>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList
									autoFocusItem={open}
									id="add-checklist-menu"
									aria-labelledby="add-checklist-button"
								>
									<MenuItem onClick={handleSubmit}>
										<Translate String="Procedure.Checklist.New.Manual">
											New manual checklist
										</Translate>
									</MenuItem>

									{hasDocument(props.item) && (
										<MenuItem
											onClick={(e) => {
												handleClose(e);
												toggleNewChecklist(true);
											}}
										>
											<Translate String="Procedure.Checklist.New.Imported">
												New imported checklist
											</Translate>
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>

			{/* MODAL: imported checklist */}
			<NewChecklistModal
				open={newChecklistOpen}
				selectedNode={props.item}
				onClose={() => toggleNewChecklist(false)}
			/>
		</>
	);
};

/**
 * ProcedureAccordionSubMenuItem component renders a single item in the procedure accordion.
 * It displays the procedure details, including the name, status, and timing information.
 * The component also allows for interaction to move the item up or down in the list.
 *
 * @component
 * @param {IAccordionItem} props - The properties for the accordion item.
 * @param {number} props.activeSchemeId - The ID of the currently active scheme.
 * @param {Object} props.item - The item data for the accordion.
 * @param {string} props.item.Name - The name of the procedure item.
 * @param {number} props.item.SchemeId - The ID of the scheme.
 * @param {string} props.item.CheckListStarted - The start time of the checklist in UTC format.
 * @param {string} props.item.CheckListLastAction - The last action time of the checklist in UTC format.
 * @param {string} props.item.TargetCompletionTime - The target completion time in "HH:mm:ss" format.
 * @param {string} props.item.CompletedState - The completion state of the item ("Done" or other states).
 * @param {number} props.item.Done - The number of completed tasks.
 * @param {number} props.item.Total - The total number of tasks.
 * @param {boolean} props.readonly - Flag indicating if the item is read-only.
 * @param {function} props.setActiveSchemeId - Function to set the active scheme ID.
 * @param {function} props.toggleEditNode - Function to toggle the edit node state.
 * @param {function} props.toggleEditChecklist - Function to toggle the edit checklist state.
 * @param {function} moveAccordionItem - Function to move the accordion item up or down.
 * @param {function} UpdateProcedureDetailsTree - Function to update the procedure details tree.
 * @returns {JSX.Element} The rendered ProcedureAccordionSubMenuItem component.
 */
const ProcedureAccordionSubMenuItem: FC<IAccordionItem> = (props) => {
	const { id, schemeId } = useParams<RouteParams>();
	const updateProcedureDetailsTree = UpdateProcedureDetailsTree();

	const isActive = +schemeId! === props.item.SchemeId;
	const navigate = useNavigate();

	if (!props.item) {
		return <></>;
	}
	// Calculate duration since checklist started and last action
	const durationSinceStartInAction = dayjs.duration(
		dayjs.utc().diff(dayjs.utc(props.item.CheckListStarted)),
	);

	const durationSinceStartDone = dayjs.duration(
		dayjs
			.utc(props.item.CheckListLastAction)
			.diff(dayjs.utc(props.item.CheckListStarted)),
	);

	// Helper function to parse "HH:mm:ss" strings into milliseconds
	function parseTimeStringToMilliseconds(timeString: string) {
		const [hours, minutes, seconds] = timeString.split(":").map(Number);
		return dayjs.duration({ hours, minutes, seconds }).asMilliseconds();
	}

	// Determine the appropriate duration based on completion state
	const durationSinceStart =
		props.item.CompletedState === "Done"
			? durationSinceStartDone
			: durationSinceStartInAction;

	// Format durationSinceStart as HH:mm:ss for checklist timer
	const checklistTimer = `${Math.floor(durationSinceStart.asHours())
		.toString()
		.padStart(2, "0")}:${durationSinceStart
		.minutes()
		.toString()
		.padStart(2, "0")}:${durationSinceStart
		.seconds()
		.toString()
		.padStart(2, "0")}`;

	// Parse the target completion time in "HH:mm:ss" format to milliseconds
	const targetCompletionTimeMilliseconds = parseTimeStringToMilliseconds(
		props.item.TargetCompletionTime,
	);

	// Calculate the difference in milliseconds
	const differenceMilliseconds =
		durationSinceStart.asMilliseconds() - targetCompletionTimeMilliseconds;

	// Convert the difference to a duration object
	const differenceDuration = dayjs.duration(Math.abs(differenceMilliseconds));

	// Format the difference as HH:mm:ss
	const differenceFormatted = `${differenceMilliseconds < 0 ? "-" : ""}${Math.floor(
		differenceDuration.asHours(),
	)
		.toString()
		.padStart(2, "0")}:${differenceDuration
		.minutes()
		.toString()
		.padStart(2, "0")}:${differenceDuration
		.seconds()
		.toString()
		.padStart(2, "0")}`;
	return (
		<>
			<Grid
				container
				spacing={2}
				sx={{
					backgroundColor: isActive
						? "rgb(232,236,244)"
						: props.item.SchemeId % 2 === 0
							? "transparent"
							: "rgba(0, 0, 0, 0.04)",
					borderRadius: 2,
					transition: "all .2s ease",
					alignItems: "center",
					mb: 1,
					mt: 2,
					ml: -1,
					p: 2,
				}}
			>
				<Box
					onClick={() => {
						props.setActiveSchemeId(props.item.SchemeId);
						props.toggleEditNode(false);
						props.toggleEditChecklist(false);
					}}
					sx={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						mt: -2,
						mb: -2,
					}}
				>
					<Grid
						container
						alignItems="center"
						justifyContent="space-between"
						spacing={1}
					>
						<Grid item xs zeroMinWidth>
							<Tooltip title={props.item.Name}>
								<Typography
									sx={{
										fontSize: 16,
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
										color: "primary.main",
										maxWidth: "100%",
									}}
								>
									{props.item.Name.length > 50
										? `${props.item.Name.substring(0, 50)}...` // Truncate long names to keep the screen not overflowing
										: props.item.Name}
								</Typography>
							</Tooltip>
						</Grid>

						<Grid item sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<Chip
								label={`${props.item.Done} / ${props.item.Total}`}
								sx={{ fontWeight: "bold", fontSize: "0.875rem", mr: 0.5 }}
							/>
							<Tooltip title={props.item.CompletedState}>
								<Box
									className={`accordion-item-status small ${calculateStatusColor(
										props.item.CompletedState,
									)}`}
									sx={{
										width: "16px",
										height: "16px",
										borderRadius: "50%",
										backgroundColor: calculateStatusColor(
											props.item.CompletedState,
										),
									}}
								/>
							</Tooltip>
							{!props.readonly && (
								<Box
									sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
								>
									<IconButton
										size="small"
										onClick={(e) =>
											moveAccordionItem(
												e,
												props.item,
												"Up",
												parseInt(id!),
												updateProcedureDetailsTree,
											)
										}
									>
										<KeyboardArrowUpIcon fontSize="small" />
									</IconButton>

									<IconButton
										size="small"
										onClick={(e) =>
											moveAccordionItem(
												e,
												props.item,
												"Down",
												parseInt(id!),
												updateProcedureDetailsTree,
											)
										}
									>
										<KeyboardArrowDownIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
						</Grid>
					</Grid>
					{props.item.TargetCompletionTime !== "00:00:00" && (
						<Grid
							container
							alignItems="center"
							justifyContent="space-between"
							spacing={1}
							mb={1}
						>
							<Grid
								item
								xs={12}
								sm={6}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									flexWrap: "wrap",
								}}
							>
								<AccessTimeIcon color="primary" />
								<Typography variant="body2" color="textSecondary">
									Target Time:
								</Typography>
								<Typography variant="body2">
									{props.item.TargetCompletionTime}
								</Typography>
							</Grid>
							<Grid
								item
								xs={12}
								sm={6}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									flexWrap: "wrap",
								}}
							>
								{props.item.CompletedState === "Done" ? (
									differenceMilliseconds > 0 ? (
										<Typography
											variant="body2"
											color="error"
											sx={{ display: "flex", alignItems: "center" }}
										>
											<FastRewindIcon sx={{ mr: 0.5 }} /> Over by{" "}
											{differenceFormatted}
										</Typography>
									) : (
										<Typography
											variant="body2"
											color="success.main"
											sx={{ display: "flex", alignItems: "center" }}
										>
											<FastForwardIcon sx={{ mr: 0.5 }} /> Faster by{" "}
											{differenceFormatted.slice(1)}
										</Typography>
									)
								) : (
									<>
										<Typography variant="body2" color="textSecondary">
											Current Time:
										</Typography>
										<Typography variant="body2">{checklistTimer}</Typography>
									</>
								)}
							</Grid>
						</Grid>
					)}
				</Box>
			</Grid>
		</>
	);
};

export const calculateStatusColor = (state: ProcedureDetailNodeState) => {
	if (state === "NotStarted") {
		return red[500];
	}

	if (state === "Started") {
		return yellow[700];
	}

	if (state === "Done") {
		return green[500];
	}

	if (state === "Locked") {
		return grey[700];
	}

	if (state === "Error") {
		return red[500];
	}
};
